"use client"

/**
 * Shared Portable Text Custom Type Renderers
 *
 * Extracted to prevent drift between blog (SanityProseText) and guide (PortableText)
 * renderers. All custom block types that Content OS can publish are defined here.
 */

import Image from "next/image"
import { Info, AlertTriangle, Lightbulb, ShieldAlert } from "lucide-react"
import { urlForImage } from "@/sanity/lib/image"
import { MermaidDiagram } from "@/core/components/mermaid-diagram"

// ═══════════════════════════════════════════════════════════════
// Image Block
// ═══════════════════════════════════════════════════════════════

export function ImageBlock({
  value,
  className = "my-12",
}: {
  value: { asset: { _id: string; url: string }; alt?: string; caption?: string }
  className?: string
}) {
  if (!value?.asset?.url) return null
  return (
    <figure className={className}>
      <div className="relative w-full aspect-video rounded-lg overflow-hidden">
        <Image
          src={urlForImage(value.asset).width(1200).quality(85).url()}
          alt={value.alt || ""}
          fill
          className="object-cover"
          sizes="(max-width: 768px) 100vw, 800px"
        />
      </div>
      {value.caption && (
        <figcaption className="mt-3 text-center text-sm text-slate-500 italic">
          {value.caption}
        </figcaption>
      )}
    </figure>
  )
}

// ═══════════════════════════════════════════════════════════════
// Code Block
// ═══════════════════════════════════════════════════════════════

export function CodeBlockSimple({
  value,
}: {
  value: { language?: string; code: string; filename?: string }
}) {
  return (
    <pre>
      <code className={`language-${value.language || "text"}`}>
        {value.code}
      </code>
    </pre>
  )
}

export function CodeBlockPremium({
  value,
}: {
  value: { language?: string; code: string; filename?: string }
}) {
  return (
    <div className="my-10 rounded-xl overflow-hidden border border-white/10 bg-[#0B0E14] shadow-2xl relative group">
      {/* macOS Top Bar */}
      <div className="flex items-center px-4 py-3 bg-white/[0.02] border-b border-white/5">
        <div className="flex items-center gap-1.5 mr-4">
          <div className="w-3 h-3 rounded-full bg-slate-600/50 group-hover:bg-[#FF5F56] transition-colors" />
          <div className="w-3 h-3 rounded-full bg-slate-600/50 group-hover:bg-[#FFBD2E] transition-colors" />
          <div className="w-3 h-3 rounded-full bg-slate-600/50 group-hover:bg-[#27C93F] transition-colors" />
        </div>
        {value.filename && (
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">
            {value.filename}
          </div>
        )}
        {value.language && (
          <div className="ml-auto text-[10px] text-slate-600 font-mono uppercase border border-slate-700/50 px-2 py-0.5 rounded backdrop-blur">
            {value.language}
          </div>
        )}
      </div>
      <pre className="p-5 overflow-x-auto selection:bg-premium-cyan/30">
        <code className={`language-${value.language || "text"} text-[13px] leading-tight font-mono text-slate-300`}>
          {value.code}
        </code>
      </pre>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Callout Block
// ═══════════════════════════════════════════════════════════════

const CALLOUT_STYLES = {
  info: "border-blue-500/30 bg-blue-500/5 text-blue-200",
  note: "border-blue-500/30 bg-blue-500/5 text-blue-200",
  warning: "border-amber-500/30 bg-amber-500/5 text-amber-200",
  caution: "border-amber-500/30 bg-amber-500/5 text-amber-200",
  danger: "border-rose-500/30 bg-rose-500/5 text-rose-200",
  tip: "border-emerald-500/30 bg-emerald-500/5 text-emerald-200",
  success: "border-emerald-500/30 bg-emerald-500/5 text-emerald-200",
  important: "border-purple-500/30 bg-purple-500/5 text-purple-200",
} as const

const CALLOUT_ICONS = {
  info: Info,
  note: Info,
  warning: AlertTriangle,
  caution: AlertTriangle,
  danger: ShieldAlert,
  tip: Lightbulb,
  success: Lightbulb,
  important: ShieldAlert,
} as const

export function CalloutBlock({
  value,
}: {
  value: { type?: string; text?: string }
}) {
  if (!value?.text) return null
  const calloutType = (value.type || "info").toLowerCase() as keyof typeof CALLOUT_ICONS
  const Icon = CALLOUT_ICONS[calloutType] || Info
  const style = CALLOUT_STYLES[calloutType as keyof typeof CALLOUT_STYLES] || CALLOUT_STYLES.info

  return (
    <div className={`my-8 p-5 rounded-xl border-l-4 flex items-start gap-3 ${style}`}>
      <Icon className="w-5 h-5 mt-0.5 shrink-0 opacity-80" />
      <span className="text-sm leading-relaxed">{value.text}</span>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Mermaid Diagram
// ═══════════════════════════════════════════════════════════════

export function DiagramBlock({
  value,
}: {
  value: { code: string; caption?: string }
}) {
  if (!value?.code) return null
  return <MermaidDiagram code={value.code} caption={value.caption} />
}

// ═══════════════════════════════════════════════════════════════
// Comparison Table
// ═══════════════════════════════════════════════════════════════

export function ComparisonTableBlock({
  value,
}: {
  value: { headers: string[]; rows: { cells: string[] }[] }
}) {
  if (!value?.headers || !value?.rows) return null
  return (
    <div className="my-12 relative overflow-hidden rounded-xl border border-white/[0.08] bg-black/40 backdrop-blur-3xl shadow-2xl">
      <div className="absolute inset-0 bg-gradient-to-br from-premium-cyan/5 to-transparent pointer-events-none" />
      <div className="overflow-x-auto relative z-10 w-full no-scrollbar">
        <table className="w-full text-sm whitespace-nowrap min-w-[600px]">
          <thead>
            <tr className="border-b border-white/10 bg-white/[0.03]">
              {value.headers.map((header, i) => (
                <th
                  key={i}
                  className="px-6 py-5 text-left text-[11px] font-bold uppercase tracking-widest text-premium-cyan font-mono"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.04]">
            {value.rows.map((row, ri) => (
              <tr
                key={ri}
                className="hover:bg-white/[0.02] transition-colors"
              >
                {row.cells?.map((cell, ci) => (
                  <td
                    key={ci}
                    className={`px-6 py-4 font-light leading-relaxed ${
                      ci === 0
                        ? "text-white font-medium"
                        : "text-slate-400 font-mono text-[13px]"
                    }`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
